import React, { useState } from 'react'
import { Calendar } from 'lucide-react'
import CalendarModal, { DateRange } from './CalendarModal'

interface CalendarButtonProps {
  value: DateRange
  onChange: (range: DateRange) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
  title?: string
}

const CalendarButton: React.FC<CalendarButtonProps> = ({
  value,
  onChange,
  size = 'md',
  className = '',
  title = "Select Date Range"
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Size configurations
  const sizeConfig = {
    sm: {
      button: 'px-3 py-2 text-sm',
      icon: 'w-4 h-4'
    },
    md: {
      button: 'px-4 py-2 text-sm',
      icon: 'w-4 h-4'
    },
    lg: {
      button: 'px-6 py-3 text-base',
      icon: 'w-5 h-5'
    }
  }

  const config = sizeConfig[size]

  // Format display text
  const getDisplayText = () => {
    if (value.label) {
      return value.label
    }
    
    if (!value.startDate || !value.endDate) {
      return 'Select date range'
    }
    
    const start = new Date(value.startDate)
    const end = new Date(value.endDate)
    
    if (value.startDate === value.endDate) {
      return start.toLocaleDateString('en-GB')
    }
    
    return `${start.toLocaleDateString('en-GB')} - ${end.toLocaleDateString('en-GB')}`
  }

  const handleModalChange = (newRange: DateRange) => {
    onChange(newRange)
    setIsModalOpen(false)
  }

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`${config.button} border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center space-x-2 transition-colors ${className}`}
      >
        <Calendar className={`${config.icon} text-gray-500`} />
        <span className="text-gray-700 truncate max-w-32">
          {getDisplayText()}
        </span>
      </button>

      <CalendarModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        value={value}
        onChange={handleModalChange}
        title={title}
      />
    </>
  )
}

export default CalendarButton
